import React, { useEffect, useState } from "react";
import { Cookie, X, Check, ShieldCheck } from "lucide-react";
import {
  readConsent,
  persistConsent,
  updateConsent,
  applyStoredConsent,
} from "../lib/gtag";

/**
 * UK-GDPR / PECR compliant consent banner with Google Consent Mode v2.
 *
 * - Banner is hidden on /admin and /staff-portal-* (those routes track nothing
 *   that needs consent).
 * - "Accept all" grants ad_storage + analytics_storage.
 * - "Reject all" keeps the index.html-set defaults (denied) and just persists
 *   the choice so the banner doesn't reappear.
 * - "Manage" exposes the two toggles separately.
 */
const EXCLUDED_PREFIXES = ["/admin", "/staff-portal-"];

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  useEffect(() => {
    // Apply any stored consent so gtag respects prior choice for SPA navigation.
    applyStoredConsent();

    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (EXCLUDED_PREFIXES.some((p) => path.startsWith(p))) return;

    const stored = readConsent();
    if (!stored) setVisible(true);
  }, []);

  const acceptAll = () => {
    updateConsent({ analytics: true, ads: true });
    persistConsent({ analytics: true, ads: true });
    setVisible(false);
  };

  const rejectAll = () => {
    updateConsent({ analytics: false, ads: false });
    persistConsent({ analytics: false, ads: false });
    setVisible(false);
  };

  const saveCustom = () => {
    updateConsent({ analytics, ads });
    persistConsent({ analytics, ads });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie & tracking preferences"
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[100] bg-[#0B1220]/95 backdrop-blur-sm border-t border-[#00C2D6]/30 shadow-[0_-8px_30px_-12px_rgba(0,194,214,0.25)]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
        {!showManage ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <Cookie size={20} className="text-[#A6FF00] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[#F7FAFC] text-sm font-semibold pg-heading mb-1">
                  We use cookies for analytics & advertising
                </div>
                <div className="text-[#9CA8B8] text-[12px] leading-relaxed">
                  Essential cookies are always on (basket, login). With your consent
                  we also enable Google Analytics &amp; Google Ads measurement so we
                  can improve the site and offer relevant promotions.{" "}
                  <a
                    href="/legal/privacy"
                    className="text-[#5EEAD4] hover:underline underline-offset-2"
                  >
                    Privacy policy
                  </a>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setShowManage(true)}
                className="pg-btn-ghost text-[10px] tracking-[0.16em] uppercase"
                data-testid="cookie-manage-btn"
              >
                Manage
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="pg-btn-ghost text-[10px] tracking-[0.16em] uppercase"
                data-testid="cookie-reject-btn"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-btn"
                className="text-[10px] font-extrabold tracking-[0.16em] uppercase rounded-sm px-3.5 py-2 whitespace-nowrap"
                style={{
                  background: "#A6FF00",
                  color: "#0B1220",
                  border: 0,
                  cursor: "pointer",
                }}
              >
                Accept all
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ShieldCheck size={18} className="text-[#5EEAD4]" />
                <div className="text-[#F7FAFC] text-sm pg-heading">
                  Cookie preferences
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowManage(false)}
                aria-label="Close"
                className="text-[#9CA8B8] hover:text-[#F7FAFC]"
              >
                <X size={16} />
              </button>
            </div>

            <ConsentRow
              label="Strictly necessary"
              description="Basket, login session, security. Cannot be turned off."
              checked={true}
              disabled={true}
              onChange={() => {}}
            />
            <ConsentRow
              label="Analytics"
              description="Anonymised page views & traffic patterns to improve the site."
              checked={analytics}
              onChange={setAnalytics}
              testid="cookie-toggle-analytics"
            />
            <ConsentRow
              label="Advertising"
              description="Google Ads measurement & remarketing audiences. Powers our promotions."
              checked={ads}
              onChange={setAds}
              testid="cookie-toggle-ads"
            />

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#1E2E44]">
              <button
                type="button"
                onClick={rejectAll}
                className="pg-btn-ghost text-[10px] tracking-[0.16em] uppercase"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={saveCustom}
                data-testid="cookie-save-prefs"
                className="text-[10px] font-extrabold tracking-[0.16em] uppercase rounded-sm px-3.5 py-2"
                style={{
                  background: "#A6FF00",
                  color: "#0B1220",
                  border: 0,
                  cursor: "pointer",
                }}
              >
                <Check size={12} className="inline mr-1 -mt-0.5" /> Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRow({ label, description, checked, disabled, onChange, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <div className="min-w-0">
        <div className="text-[#F7FAFC] text-[13px] font-semibold">{label}</div>
        <div className="text-[#9CA8B8] text-[11.5px] leading-snug mt-0.5">
          {description}
        </div>
      </div>
      <label
        className="relative inline-flex items-center cursor-pointer shrink-0 mt-0.5"
        style={{ opacity: disabled ? 0.55 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <input
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testid}
        />
        <div
          className="w-10 h-5 rounded-full transition-colors"
          style={{
            background: checked ? "#A6FF00" : "#1E2E44",
            border: "1px solid " + (checked ? "#A6FF00" : "#2C3E5A"),
          }}
        />
        <div
          className="absolute top-[3px] left-[3px] w-3.5 h-3.5 rounded-full bg-[#0B1220] transition-transform"
          style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
        />
      </label>
    </div>
  );
}
